import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GlassIconButton } from 'dashboard/components/common';

/**
 * Create a plan, or edit one that exists.
 *
 * Editing is split in two because Stripe is: a name and description change in
 * place, a price cannot. Repricing creates a new price and retires the old,
 * and — the part everyone gets wrong — leaves every existing subscriber on
 * what they already pay. The warning says so before the click, not after.
 */

const CURRENCIES = ['eur', 'usd', 'gbp', 'czk'];
const INTERVALS = [
  { value: 'month', label: 'Monthly' },
  { value: 'year', label: 'Yearly' },
  { value: 'week', label: 'Weekly' },
  { value: 'day', label: 'Daily' },
];

export default function PlanDialog({ open, plan, onClose, onSubmit, busy }) {
  const editing = Boolean(plan);
  const [form, setForm] = useState({});
  const [repricing, setRepricing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRepricing(false);
    setForm({
      name: plan?.name || '',
      description: plan?.description || '',
      amount: plan?.current_price?.amount ?? '',
      currency: (plan?.current_price?.currency || 'EUR').toLowerCase(),
      interval: plan?.current_price?.interval || 'month',
    });
  }, [open, plan]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const priceChanged = editing && (
    String(form.amount) !== String(plan?.current_price?.amount ?? '')
    || form.interval !== (plan?.current_price?.interval || 'month')
    || form.currency !== (plan?.current_price?.currency || 'EUR').toLowerCase()
  );

  const valid = String(form.name || '').trim()
    && form.amount !== '' && Number(form.amount) >= 0;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ pr: '48px' }}>
        {editing ? 'Edit plan' : 'New plan'}
        {!busy && (
          <GlassIconButton
            label='Close'
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            size='small'
          >
            <CloseIcon fontSize='small' />
          </GlassIconButton>
        )}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing='14px'>
          <TextField
            size='small'
            label='Plan name'
            value={form.name || ''}
            onChange={(e) => set({ name: e.target.value })}
            placeholder='Pro'
            fullWidth
            required
          />
          <TextField
            size='small'
            label='Description'
            value={form.description || ''}
            onChange={(e) => set({ description: e.target.value })}
            placeholder='Everything in Starter, plus custom domains and unlimited pages.'
            multiline
            minRows={2}
            fullWidth
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing='10px'>
            <TextField
              size='small'
              type='number'
              label='Price'
              value={form.amount}
              onChange={(e) => set({ amount: e.target.value })}
              inputProps={{ min: 0, step: '0.01' }}
              disabled={editing && !repricing}
              sx={{ flex: 1 }}
            />
            <FormControl size='small' sx={{ width: { sm: 120 } }} disabled={editing && !repricing}>
              <InputLabel>Currency</InputLabel>
              <Select
                label='Currency'
                value={form.currency || 'eur'}
                onChange={(e) => set({ currency: e.target.value })}
              >
                {CURRENCIES.map((c) => (
                  <MenuItem key={c} value={c}>{c.toUpperCase()}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size='small' sx={{ width: { sm: 140 } }} disabled={editing && !repricing}>
              <InputLabel>Billing</InputLabel>
              <Select
                label='Billing'
                value={form.interval || 'month'}
                onChange={(e) => set({ interval: e.target.value })}
              >
                {INTERVALS.map((i) => (
                  <MenuItem key={i.value} value={i.value}>{i.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* Repricing is behind a deliberate second step. Stripe cannot edit
              a price, so this always creates a new one — a heavier action
              than renaming, and worth an explicit choice. */}
          {editing && !repricing && (
            <Box>
              <Button size='small' onClick={() => setRepricing(true)}>
                Change the price
              </Button>
            </Box>
          )}

          {editing && repricing && priceChanged && (
            <Alert severity='warning' sx={{ borderRadius: '10px' }}>
              This creates a new price and stops offering the old one.{' '}
              <strong>Everyone already subscribed keeps paying what they pay now</strong> — only
              new customers get this price. Moving existing subscribers has to be done per
              subscription.
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: '14px 20px' }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant='contained'
          disabled={!valid || busy}
          onClick={() => onSubmit({
            product_id: plan?.id,
            name: form.name,
            description: form.description,
            amount: Number(form.amount),
            currency: form.currency,
            interval: form.interval,
            reprice: repricing && priceChanged,
          })}
        >
          {busy ? 'Saving…' : editing ? 'Save' : 'Create plan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
